import { Injectable } from '@angular/core';
import { BaseRestService } from '../base-rest.service';
import { HttpClient } from '@angular/common/http';
import { RequestCacheService } from '../core/request-cache.service';
import { SessionStorageService } from '../session-storage.service';
import { ImageUrlModel } from '../../models/entity/image-url.model';
import { catchError, Observable, of, tap } from 'rxjs';
import { CustomerType } from '../../models/entity/customerType';
import { CommunityEntity } from '../../models/entity/customer/community-entity';
import { ProductEntity } from '../../models/entity/product/product.entity';
import { User } from '../../models/entity/user';
import { OrderItemType } from '../../models/entity/product/orderItemType';
import { ProductPriceTabDto } from '../../models/dto/productPriceTabDto';
import { Note } from '../../models/entity/note';
import { NoteView } from '../../models/dto/noteView';
import { BonusEntity } from '../../models/entity/bonus.entity';
import { AppCultureInfoModel } from '../../models/entity/app-culture-info.model';
import { PriceListEntity } from '../../models/entity/product/price-list.entity';
import { CashRegisterEntity } from '../../models/entity/cash-register/cash-register-entity';
import { BankEntity } from '../../models/entity/common/bank.entity';
import { CustomerProductPriceSearchRequest } from '../../models/rest/customer/customer-product-price-search-request';
import { ProductView } from '../../models/dto/product/product-view';
import { ProductSearchRequest } from '../../models/rest/product/product-search-request';
import { ComponentEntity } from '../../models/entity/common/component.entity';
import { ProductCategoryEntity } from '../../models/entity/product/product-category.entity';
import { CommonParameterEntity } from '../../models/entity/common/common-parameter.entity';

@Injectable({
  providedIn: 'root'
})
export class DefinitionCacheService extends BaseRestService {

  constructor(
    protected httpClient: HttpClient,
    protected sessionStorageService: SessionStorageService,
    private cache: RequestCacheService
  ) {
    super(httpClient, sessionStorageService);
  }

  private generateCacheKey(url: string, body?: any): string {
    if (!body) {
      return url;
    }
    const bodyString = JSON.stringify(body);
    return `${url}_${btoa(bodyString)}`;
  }

  private getCachedResponse<T>(cacheKey: string): Observable<T> {
    const cachedResponse = this.cache.get(cacheKey);
    if (cachedResponse) {
      return of(cachedResponse);
    }
    return null!;
  }

  private fetchAndCache<T>(url: string): Observable<T> {
    const cacheKey = this.generateCacheKey(url);
    return this.httpGet<T>(url).pipe(
      tap((response) => {
        this.cache.put(cacheKey, response);
      }),
      catchError(this.errorHandler)
    );
  }

  private fetchAndCachePost<T>(url: string, body?: any): Observable<T> {
    const cacheKey = this.generateCacheKey(url, body);
    return this.httpPost<T>(url, body || {}).pipe(
      tap((response) => {
        this.cache.put(cacheKey, response);
      }),
      catchError(this.errorHandler)
    );
  }

  public getImageUrlsBySearch(categoryType: string): Observable<ImageUrlModel[]> {
    const url = `/image-urls/search?category-type=${categoryType}`;
    const cacheKey = this.generateCacheKey(url);
    return this.getCachedResponse<ImageUrlModel[]>(cacheKey) || this.fetchAndCache<ImageUrlModel[]>(url);
  }

  public getCustomerTypes(): Observable<CustomerType[]> {
    const url = `/customer-types`;
    const cacheKey = this.generateCacheKey(url);
    return this.getCachedResponse<CustomerType[]>(cacheKey) || this.fetchAndCache<CustomerType[]>(url);
  }

  public getCommunities(): Observable<CommunityEntity[]> {
    const url = `/communities`;
    const cacheKey = this.generateCacheKey(url);
    return this.getCachedResponse<CommunityEntity[]>(cacheKey) || this.fetchAndCache<CommunityEntity[]>(url);
  }

  public getPriceLists(): Observable<PriceListEntity[]> {
    const url = `/users/allowed-price-lists`;
    const cacheKey = this.generateCacheKey(url);
    return this.getCachedResponse<PriceListEntity[]>(cacheKey) || this.fetchAndCache<PriceListEntity[]>(url);
  }

  public getProducts(): Observable<ProductEntity[]> {
    const url = `/products`;
    const cacheKey = this.generateCacheKey(url);
    return this.getCachedResponse<ProductEntity[]>(cacheKey) || this.fetchAndCache<ProductEntity[]>(url);
  }
  public getProductCategories(): Observable<ProductCategoryEntity[]> {
    const url = `/product-categories`;
    const cacheKey = this.generateCacheKey(url);
    return this.getCachedResponse<ProductCategoryEntity[]>(cacheKey) || this.fetchAndCache<ProductCategoryEntity[]>(url);
  }

  public getInventoryProducts(): Observable<ProductView[]> {
    const url = `/products/search`;
    const request: ProductSearchRequest = {
      isInventory: true,
      status: 1
    };
    const cacheKey = this.generateCacheKey(url, request);
    return this.getCachedResponse<ProductView[]>(cacheKey) || this.fetchAndCachePost<ProductView[]>(url, request);
  }




  public getBonuses(): Observable<BonusEntity[]> {
    const url = `/bonuses`;
    const cacheKey = this.generateCacheKey(url);
    return this.getCachedResponse<BonusEntity[]>(cacheKey) || this.fetchAndCache<BonusEntity[]>(url);
  }

  public getAppCultureInfos(): Observable<AppCultureInfoModel[]> {
    const url = `/commons/app-culture-infos`;
    const cacheKey = this.generateCacheKey(url);
    return this.getCachedResponse<AppCultureInfoModel[]>(cacheKey) || this.fetchAndCache<AppCultureInfoModel[]>(url);
  }

  public getUsers(): Observable<User[]> {
    const url = `/users`;
    const cacheKey = this.generateCacheKey(url);
    return this.getCachedResponse<User[]>(cacheKey) || this.fetchAndCache<User[]>(url);
  }

  public getOrderItemTypes(): Observable<OrderItemType[]> {
    const url = `/order-item-types`;
    const cacheKey = this.generateCacheKey(url);
    return this.getCachedResponse<OrderItemType[]>(cacheKey) || this.fetchAndCache<OrderItemType[]>(url);
  }

  public getMainActiveOrderItemTypes(): Observable<OrderItemType[]> {
    const url = `/order-item-types/active-main`;
    const cacheKey = this.generateCacheKey(url);
    return this.getCachedResponse<OrderItemType[]>(cacheKey) || this.fetchAndCache<OrderItemType[]>(url);
  }

  public getSubActiveOrderItemTypes(): Observable<OrderItemType[]> {
    const url = `/order-item-types/active-sub`;
    const cacheKey = this.generateCacheKey(url);
    return this.getCachedResponse<OrderItemType[]>(cacheKey) || this.fetchAndCache<OrderItemType[]>(url);
  }

  public getCustomerProductPriceForItemTp(data: CustomerProductPriceSearchRequest): Observable<ProductPriceTabDto[]> {
    const url = `/customer/price/product-prices`;
    const cacheKey = this.generateCacheKey(url, data);
    return this.getCachedResponse<ProductPriceTabDto[]>(cacheKey) || this.fetchAndCachePost<ProductPriceTabDto[]>(url, data);
  }

  public getCustomerProductPriceForProduct(data: CustomerProductPriceSearchRequest): Observable<ProductPriceTabDto> {
    const url = `/customer/price/product-price`;
    const cacheKey = this.generateCacheKey(url, data);
    return this.getCachedResponse<ProductPriceTabDto>(cacheKey) || this.fetchAndCachePost<ProductPriceTabDto>(url, data);
  }

  public getNotesByType(id: number): Observable<Note[]> {
    const url = `/notes/search?note-type-id=${id}`;
    const cacheKey = this.generateCacheKey(url);
    return this.getCachedResponse<Note[]>(cacheKey) || this.fetchAndCache<Note[]>(url);
  }

  public getNotesByCategory(noteCategoryId?: number): Observable<NoteView[]> {
    const url = `/notes/search?note-category-id=${noteCategoryId}`;
    const cacheKey = this.generateCacheKey(url);
    return this.getCachedResponse<NoteView[]>(cacheKey) || this.fetchAndCache<NoteView[]>(url);
  }

  public getCashRegisterById(id?: number): Observable<CashRegisterEntity> {
    const url = `/cash-registers/${id}`;
    const cacheKey = this.generateCacheKey(url);
    return this.getCachedResponse<CashRegisterEntity>(cacheKey) || this.fetchAndCache<CashRegisterEntity>(url);
  }

  public getBanks(countryId?: number): Observable<BankEntity[]> {
    const url = `/banks?country-id=${countryId}`;
    const cacheKey = this.generateCacheKey(url);
    return this.getCachedResponse<BankEntity[]>(cacheKey) || this.fetchAndCache<BankEntity[]>(url);
  }
  public getComponents(): Observable<ComponentEntity[]> {
    const url = `/commons/components`;
    const cacheKey = this.generateCacheKey(url);
    return this.getCachedResponse<ComponentEntity[]>(cacheKey) || this.fetchAndCache<ComponentEntity[]>(url);
  }
  public getCommonParametersByCategoryType(categoryType: string): Observable<CommonParameterEntity[]> {
    const url = `/common-parameters?category-type=${categoryType}`;
    const cacheKey = this.generateCacheKey(url);
    return this.getCachedResponse<CommonParameterEntity[]>(cacheKey) || this.fetchAndCache<CommonParameterEntity[]>(url);
  }

  public clearCommonParametersCache(): void {
    const url = `/common-parameters`;
    this.cache.clearByKeyPattern(url);
  }


  // Cache temizleme metodları
  public clearImageUrlsCache(): void {
    const url = `/image-urls/search`;
    this.cache.clearByKeyPattern(url);
  }

  public clearCustomerTypesCache(): void {
    const url = `/customer-types`;
    this.cache.clearByKeyPattern(url);
  }

  public clearCommunitiesCache(): void {
    const url = `/communities`;
    this.cache.clearByKeyPattern(url);
  }

  public clearPriceListsCache(): void {
    const url = `/users/allowed-price-lists`;
    this.cache.clearByKeyPattern(url);
  }

  public clearProductsCache(): void {
    const url = `/products`;
    this.cache.clearByKeyPattern(url);
  }

  public clearBonusesCache(): void {
    const url = `/bonuses`;
    this.cache.clearByKeyPattern(url);
  }

  public clearUsersCache(): void {
    const url = `/users`;
    this.cache.clearByKeyPattern(url);
  }

  public clearOrderItemTypesCache(): void {
    const url = `/order-item-types`;
    this.cache.clearByKeyPattern(url);
  }

  public clearCustomerProductPricesCache(): void {
    const url1 = `/customer/price/product-prices`;
    const url2 = `/customer/price/product-price`;
    this.cache.clearByKeyPattern(url1);
    this.cache.clearByKeyPattern(url2);
  }

  public clearNotesCache(): void {
    const url = `/notes/search`;
    this.cache.clearByKeyPattern(url);
  }

  public clearCashRegistersCache(): void {
    const url = `/cash-registers`;
    this.cache.clearByKeyPattern(url);
  }

  public clearBanksCache(): void {
    const url = `/banks`;
    this.cache.clearByKeyPattern(url);
  }

  public clearAllCache(): void {
    this.cache.clear();
  }

  public getProductsBySearch(searchRequest: ProductSearchRequest): Observable<ProductView[]> {
    const url = `/products/search`;
    const cacheKey = this.generateCacheKey(url, searchRequest);
    return this.getCachedResponse<ProductView[]>(cacheKey) || this.fetchAndCachePost<ProductView[]>(url, searchRequest);
  }
}
